// Chat shell — the pathless layout route that OWNS the chat surface for BOTH
// '/' (new chat) and '/chat/$sessionId' (existing chat), so the two URLs share
// ONE GenUIChat instance.
//
// WHY (dev-flip P0-2). When the chat view creates a session mid-turn it swaps
// the URL '/' → '/chat/<id>' (apps/web/lib/chat-url.ts → next-compat/chat-url.ts).
// With '/' and '/chat/$sessionId' as SIBLING routes, that swap unmounted the
// home route and mounted the chat-session route: a fresh GenUIChat that
// refetched the session and raced the POST /api/chat still creating it (404 →
// "Couldn't load this conversation"), and dropped the streaming UI of the turn
// the user had just started.
//
// Sibling routes cannot share a component instance — each match renders its own.
// So the chat lives HERE, on the shared parent, and '/' + '/chat/$sessionId'
// become pure URL matchers (they render null; see index.tsx / chat.$sessionId.tsx).
// The parent match survives the URL swap, so React keeps the instance.
//
// Keeping the instance is necessary but NOT sufficient: GenUIChat also reloads
// whenever its `sessionId` PROP changes (lab-chat-view.tsx's "load messages when
// we have a session ID" effect is keyed on it). After a mint the component
// already holds that id in its own state — the URL is catching up to the
// component, not the other way round — so on a tagged swap this shell keeps
// passing the prop it mounted with (null) and lets the effect stay asleep.
//
// The tag is what separates the two cases, and it is set by the ONE call site
// that performs a mint swap:
//
//   tagged replace ('chatMinted' in history state, id matches the URL)
//       → adopt the id, keep the React key AND the prop → no remount, no refetch
//   anything else (sidebar link, new-chat, back/forward, cold load, reload)
//       → a real navigation → new key → GenUIChat remounts on the new session
//
// A reload of a minted URL is the "anything else" branch (the shell mounts
// fresh from the URL), which is why the transcript still loads after F5.
import { Suspense, useRef } from 'react';
import { createRoute, useRouterState } from '@tanstack/react-router';
import { GenUIChat } from '@/app/labs/genui/lab-chat-view';
import { rootRoute } from './__root';
import { CHAT_MINTED_STATE_KEY } from '../next-compat/chat-url';

/** '/chat/<id>' → id; anything else (incl. '/') → null. */
function sessionIdFromPath(pathname: string): string | null {
  const m = /^\/chat\/([^/]+)\/?$/.exec(pathname);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return m[1];
  }
}

/** Distinct React key per fresh-chat instance, so '/' after a chat remounts. */
let newChatEpoch = 0;

interface ChatInstance {
  /** React key — changes only on a real navigation. */
  key: string;
  /** `sessionId` prop, FROZEN at instance creation (see the effect note above). */
  prop: string | null;
  /** Session the URL currently addresses (advances on a mint, without a remount). */
  url: string | null;
}

function ChatShell() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const minted = useRouterState({
    select: (s) =>
      ((s.location.state as unknown as Record<string, unknown> | undefined)?.[
        CHAT_MINTED_STATE_KEY
      ] as string | undefined) ?? null,
  });

  const urlSessionId = sessionIdFromPath(pathname);
  const instanceRef = useRef<ChatInstance>({
    key: urlSessionId ?? `new-${newChatEpoch}`,
    prop: urlSessionId,
    url: urlSessionId,
  });
  const instance = instanceRef.current;

  // Derived-state-during-render (idempotent under StrictMode's double render:
  // the second pass sees the already-advanced `url` and does nothing).
  const isMintSwap = minted !== null && minted === urlSessionId && instance.url === null;
  if (isMintSwap) {
    // The live component minted this id; adopt it WITHOUT touching key or prop.
    instance.url = urlSessionId;
  } else if (urlSessionId !== instance.url) {
    // A real navigation — remount on the session the URL now names.
    instanceRef.current = {
      key: urlSessionId ?? `new-${++newChatEpoch}`,
      prop: urlSessionId,
      url: urlSessionId,
    };
  }

  const { key, prop } = instanceRef.current;
  return (
    // Mirrors app/page.tsx's Suspense-wrapped GenUIChat.
    <Suspense fallback={null}>
      <GenUIChat key={key} sessionId={prop} />
    </Suspense>
  );
}

export const chatShellRoute = createRoute({
  id: '_chat',
  getParentRoute: () => rootRoute,
  component: ChatShell,
});
